import { call, put, select } from 'redux-saga/effects';
import { ApplicationsBySeason } from '../actions/applicationTreatments/types/state';
import {
  fetchingApplicationTreatments,
  receiveApplicationTreatments,
} from '../actions/applicationTreatments/actions';
import { getApplicationEventAggregates } from '../queries/applicationEventAggregates';
import {
  adaptUnifiedEventsToTreatments,
  UnifiedApplicationTreatmentEvent,
} from '../utils/applicationEventAdapter';
import { ApplicationTreatment } from '../types/ApplicationTreatment';
import { getFieldUuid } from '../selectors/fieldSelectorsNew';
import { requestUnifiedApplicationEvents } from '../requesters/applicationEventAggregatesRequester';
import { selectApplicationCardEnabled } from './applicationTreatment/utils';
import { getApplicationAnalyticsQuery } from '../queries/applicationAnalyticsQuery';
import { olConnectionV2 } from '../requesters/olConnection';

function* fetchLegacyTreatments(fieldUUID: string) {
  const olResult = yield call(olConnectionV2, getApplicationAnalyticsQuery(fieldUUID));
  const eventAggregates =
    olResult.data.field.applicationAnalytics.applicationEventAggregates;

  const applicationDataBySeason: ApplicationsBySeason = {};
  eventAggregates.forEach(
    (aggregate: { seasonCode: string; applicationData: ApplicationTreatment[] }) => {
      applicationDataBySeason[aggregate.seasonCode] = aggregate.applicationData;
    },
  );

  yield put(receiveApplicationTreatments(applicationDataBySeason));

  return applicationDataBySeason;
}

type EventsBySeasonTagIndex = Record<string, UnifiedApplicationTreatmentEvent[]>;

const getEventsBySeasonTag = (events: UnifiedApplicationTreatmentEvent[]) => events.reduce<EventsBySeasonTagIndex>(
  (accum, event) => {
    if (!accum[event.legacySeasonTag]) {
      accum[event.legacySeasonTag] = [];
    }
    accum[event.legacySeasonTag].push(event);
    return accum;
  }, {});

export function* getApplicationTreatments(fieldUUID: string) {
  yield put(fetchingApplicationTreatments());

  const applicationCardEnabled: boolean = yield select(selectApplicationCardEnabled);
  if (!applicationCardEnabled) {
    return yield* fetchLegacyTreatments(fieldUUID);
  }

  const fieldUuid: string = yield select(getFieldUuid);
  const resolvedFieldId = fieldUuid || fieldUUID;

  const events: UnifiedApplicationTreatmentEvent[] = yield call(
    requestUnifiedApplicationEvents,
    getApplicationEventAggregates({ fieldId: resolvedFieldId }),
  );

  const eventsBySeasonTag = getEventsBySeasonTag(events);

  const applicationsBySeason = Object.fromEntries(
    Object.entries(eventsBySeasonTag).map(([seasonTag, seasonEvents]) => [
      seasonTag,
      adaptUnifiedEventsToTreatments(seasonEvents, seasonTag),
    ]),
  ) as ApplicationsBySeason;

  yield put(receiveApplicationTreatments(applicationsBySeason));

  return applicationsBySeason;
}
